import { ArrowLeft, Info, Plus, Truck, User, X } from 'lucide-react';
import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useSearchParams } from 'react-router-dom';
import { typography } from '../../styles/typography';
import { driverApi } from '../Driver/driverApi';
import leaveApi from '../LeaveManagement/leaveApi';
import EntitySelectionDialog from '../ScheduleARide/EntitySelectionDialog';
import { Card, PrimaryButton } from '../ui';
import { patrollingApi } from './PatrollingApi';
import SelectPatrolingEmployees from './SelectPatrolingEmployees';

const createFieldErrorState = (isOnFoot = false) => ({
  officer: false,
  vehicle: isOnFoot ? false : false, // Vehicle not required for on-foot patrol
  note: false,
  material: false,
});

const PatrollingAssignConvo = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const patrolIdParam = searchParams.get('patrolId');
  const isEditMode = !!patrolIdParam;
  
  // State for patrol officers
  const [officers, setOfficers] = useState([]);
  
  // State for available resources
  const [availableOfficers, setAvailableOfficers] = useState([]);
  const [availableVehicles, setAvailableVehicles] = useState([]);
  
  
  // State for counts
  const [totalOfficersAvailable, setTotalOfficersAvailable] = useState(-1); // -1 means loading
  const [totalOfficersAssigned, setTotalOfficersAssigned] = useState(1);
  const [totalVehiclesAvailable, setTotalVehiclesAvailable] = useState(-1); // -1 means loading
  
  // State for preview mode
  const [isPreview, setIsPreview] = useState(false);
  
  // State for patrol data (from URL params or localStorage)
  const [patrolData, setPatrolData] = useState(null);
  
  // Check if patrol type is "on-foot" (hide vehicle sections)
  const isOnFootPatrol = patrolData?.patrol_type === 'on-foot';
  
  // State for dialogs
  const [showOfficerDialog, setShowOfficerDialog] = useState(false);
  const [showVehicleDialog, setShowVehicleDialog] = useState(false);
  const [currentOfficerIndex, setCurrentOfficerIndex] = useState(null);
  const [showPatrolInfoDialog, setShowPatrolInfoDialog] = useState(false);
  
  // Loading and error states
  const [isLoading, setIsLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [validationMessage, setValidationMessage] = useState('');
  const [fieldErrors, setFieldErrors] = useState([]);

  // State for original patrol data (in edit mode)
  const [originalPatrol, setOriginalPatrol] = useState(null);
  
  // Cache for employee/user details to avoid repeated API calls
  const employeeDetailsCacheRef = useRef(new Map()); // Map<userId, { user_name, timestamp }>

  // Load patrol data from URL params or localStorage
  useEffect(() => {
    const patrolDataParam = searchParams.get('patrolData');
    if (patrolDataParam) {
      try {
        const decoded = JSON.parse(decodeURIComponent(patrolDataParam));
        setPatrolData(decoded);
        
        // In edit mode, extract original patrol data if available
        if (decoded.originalPatrol) {
          setOriginalPatrol(decoded.originalPatrol);
        }
      } catch (e) {
        console.error('Error parsing patrol data:', e);
        // Try to get from localStorage as fallback
        const stored = localStorage.getItem('pendingPatrolData');
        if (stored) {
          try {
            const parsed = JSON.parse(stored);
            setPatrolData(parsed);
            if (parsed.originalPatrol) {
              setOriginalPatrol(parsed.originalPatrol);
            }
          } catch (e2) {
            console.error('Error parsing stored patrol data:', e2);
            setError('Failed to load patrol data. Please go back and create the patrol again.');
          }
        }
      }
    } else {
      // Try to get from localStorage
      const stored = localStorage.getItem('pendingPatrolData');
      if (stored) {
        try {
          const parsed = JSON.parse(stored);
          setPatrolData(parsed);
          if (parsed.originalPatrol) {
            setOriginalPatrol(parsed.originalPatrol);
          }
        } catch (e) {
          console.error('Error parsing stored patrol data:', e);
          setError('Failed to load patrol data. Please go back and create the patrol again.');
        }
      } else {
        setError('No patrol data found. Please go back and create the patrol again.');
      }
    }
  }, [searchParams]);

  // Fetch initial data and add first officer or load existing convoy
  useEffect(() => {
    if (patrolData) {
      fetchInitialData();
    }
  }, [patrolData]);

  // Separate effect for loading existing convoy in edit mode
  useEffect(() => {
    if (patrolData && isEditMode && patrolIdParam && !isLoading) {
      // Only load convoy after initial data is fetched
      console.log('🔄 Triggering loadExistingConvoy...');
      loadExistingConvoy();
    } else if (patrolData && !isEditMode && !isLoading && officers.length === 0) {
      // In create mode, add first officer after initial data is loaded
      console.log('➕ Adding first officer for create mode...');
      addFirstOfficer();
    }
  }, [patrolData, isEditMode, patrolIdParam, isLoading]);

  // Debug effect to log officers state changes
  useEffect(() => {
    if (officers.length > 0) {
      console.log('👥 Officers state updated:', officers);
      officers.forEach((officer, idx) => {
        console.log(`Officer group ${idx + 1}:`, {
          vehicle: officer.vehicle,
          officers: officer.officers,
          note: officer.note,
          material: officer.material
        });
      });
    }
  }, [officers]);

  // Fetch available officers and vehicles
  const fetchInitialData = async () => {
    if (!patrolData) return;
    
    setIsLoading(true);
    setError(null);
    
    try {
      const startDate = patrolData.start_date;
      const startTime = patrolData.start_time;
      const endDate = patrolData.end_date;
      const endTime = patrolData.end_time;
      const siteId = patrolData.site_id;

      // TODO: Replace with actual patrolling API when available
      // For now, fetch available drivers (officers) using the driver API with date/time filtering
      const officersData = await driverApi.fetchAvailableDrivers({
        fromDate: startDate,
        fromTime: startTime,
        toDate: endDate,
        toTime: endTime,
      });
      
      const uniqueOfficers = [];
      const officerIds = new Set();
      
      officersData.forEach(officer => {
        const id = officer.user_id?.toString() || officer.id?.toString();
        if (id && !officerIds.has(id)) {
          officerIds.add(id);
          uniqueOfficers.push({
            user_id: id,
            user_name: officer.user_name || officer.name || 'Unknown',
            spec_role: officer.spec_role || 'Officer',
          });
        }
      });
      
      // In edit mode, also include officers that are already assigned to this patrol
      if (isEditMode && patrolIdParam && officers.length > 0) {
        officers.forEach(entry => {
          if (entry.officers && Array.isArray(entry.officers)) {
            entry.officers.forEach(off => {
              if (off.id) {
                const officerId = off.id.toString();
                if (!officerIds.has(officerId)) {
                  officerIds.add(officerId);
                  uniqueOfficers.push({
                    user_id: officerId,
                    user_name: off.name || 'Unknown',
                    spec_role: 'Officer',
                  });
                }
              }
            });
          }
        });
      }
      
      uniqueOfficers.sort((a, b) => 
        (a.user_name || '').localeCompare(b.user_name || '')
      );
      setAvailableOfficers(uniqueOfficers);
      setTotalOfficersAvailable(uniqueOfficers.length);

      // Fetch available vehicles based on site_id (skip if on-foot patrol)
      const isOnFoot = patrolData.patrol_type === 'on-foot';
      if (siteId && !isOnFoot) {
        try {
          const vehiclesData = await driverApi.fetchAvailableVehicles({
            fromSiteId: siteId,
            toSiteId: siteId, // Use same site_id for both since patrol is within same site
            fromDate: startDate,
            fromTime: startTime,
            toDate: endDate,
            toTime: endTime,
          });
          
          const uniqueVehicles = [];
          const vehicleIds = new Set();
          
          vehiclesData.forEach(vehicle => {
            const id = vehicle.vehicle_id?.toString() || vehicle.id?.toString();
            if (id && !vehicleIds.has(id)) {
              vehicleIds.add(id);
              uniqueVehicles.push({
                vehicle_id: id,
                registration_no: vehicle.registration_no || vehicle.model_name || 'Unknown',
                model_name: vehicle.model_name || 'Unknown',
                vehicle_type: vehicle.vehicle_type || 'Patrol',
              });
            }
          });
          
          // In edit mode, also include vehicles that are already assigned to this patrol
          if (isEditMode && patrolIdParam && officers.length > 0) {
            officers.forEach(entry => {
              if (entry.vehicle && entry.vehicle.id && !vehicleIds.has(entry.vehicle.id)) {
                vehicleIds.add(entry.vehicle.id);
                uniqueVehicles.push({
                  vehicle_id: entry.vehicle.id,
                  registration_no: entry.vehicle.name || 'Unknown',
                  model_name: entry.vehicle.name || 'Unknown',
                  vehicle_type: 'Patrol',
                });
              }
            });
          }
          
          uniqueVehicles.sort((a, b) => 
            (a.model_name || '').localeCompare(b.model_name || '')
          );
          setAvailableVehicles(uniqueVehicles);
          setTotalVehiclesAvailable(uniqueVehicles.length);
          
          console.log(`Fetched ${uniqueVehicles.length} vehicles for site ${siteId}`);
        } catch (vehicleError) {
          console.error('Error fetching vehicles:', vehicleError);
          // Don't fail the entire load if vehicles fail - just log and continue
          setAvailableVehicles([]);
          setTotalVehiclesAvailable(0);
        }
      } else if (isOnFoot) {
        // For on-foot patrol, don't fetch vehicles
        console.log('On-foot patrol: Skipping vehicle fetch');
        setAvailableVehicles([]);
        setTotalVehiclesAvailable(0);
      } else {
        console.warn('No site_id found in patrol data. Cannot fetch vehicles.');
        setAvailableVehicles([]);
        setTotalVehiclesAvailable(0);
      }

    } catch (err) {
      console.error('Error fetching initial data:', err);
      setError('Failed to load officers. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  // Update available vehicles to include already-assigned ones (for edit mode)
  useEffect(() => {
    if (isEditMode && officers.length > 0) {
      // Add already-assigned vehicles to available vehicles if not already present
      // Also update vehicle names if they've been fetched
      setAvailableVehicles(prevVehicles => {
        const vehicleIds = new Set(prevVehicles.map(v => (v.vehicle_id || v.id)?.toString()));
        const newVehicles = [...prevVehicles];
        let hasChanges = false;
        
        officers.forEach(entry => {
          if (entry.vehicle && entry.vehicle.id) {
            const vehicleId = entry.vehicle.id.toString();
            const existingVehicle = newVehicles.find(v => (v.vehicle_id || v.id)?.toString() === vehicleId);
            
            if (!existingVehicle) {
              // Vehicle not in list, add it (only if we have a name)
              if (entry.vehicle.name && entry.vehicle.name !== 'Unknown') {
                vehicleIds.add(vehicleId);
                newVehicles.push({
                  vehicle_id: vehicleId,
                  registration_no: entry.vehicle.name,
                  model_name: entry.vehicle.name,
                  vehicle_type: 'Patrol',
                });
                hasChanges = true;
              }
            } else if (entry.vehicle.name && entry.vehicle.name !== 'Unknown' && 
                       existingVehicle.model_name !== entry.vehicle.name) {
              // Vehicle exists but name has been updated, update it
              existingVehicle.model_name = entry.vehicle.name;
              existingVehicle.registration_no = entry.vehicle.name;
              hasChanges = true;
            }
          }
        });
        
        if (hasChanges) {
          newVehicles.sort((a, b) => 
            (a.model_name || '').localeCompare(b.model_name || '')
          );
          setTotalVehiclesAvailable(newVehicles.length);
          return newVehicles;
        }
        return prevVehicles;
      });
    }
  }, [isEditMode, officers]);

  // Update available officers to include already-assigned ones (for edit mode)
  useEffect(() => {
    if (isEditMode && officers.length > 0 && availableOfficers.length > 0) {
      // Add already-assigned officers to available officers if not already present
      const officerIds = new Set(availableOfficers.map(o => (o.user_id || o.id)?.toString()));
      const newOfficers = [...availableOfficers];
      
      officers.forEach(entry => {
        if (entry.officers && Array.isArray(entry.officers)) {
          entry.officers.forEach(off => {
            if (off.id) {
              const officerId = off.id.toString();
              if (!officerIds.has(officerId)) {
                officerIds.add(officerId);
                newOfficers.push({
                  user_id: officerId,
                  user_name: off.name || 'Unknown',
                  spec_role: 'Officer',
                });
              }
            }
          });
        }
      });
      
      if (newOfficers.length !== availableOfficers.length) {
        newOfficers.sort((a, b) => 
          (a.user_name || '').localeCompare(b.user_name || '')
        );
        setAvailableOfficers(newOfficers);
      }
    }
  }, [isEditMode, officers]);

  // Fetch missing officer names when officers are loaded (for edit mode)
  useEffect(() => {
    if (isEditMode && officers.length > 0) {
      const fetchMissingNames = async () => {
        let hasUpdates = false;
        const updatedOfficers = await Promise.all(
          officers.map(async (entry) => {
            if (!entry.officers || !Array.isArray(entry.officers)) {
              return entry;
            }
            
            const updatedOfficersArray = await Promise.all(
              entry.officers.map(async (off) => {
                // If we already have a valid name (not just the ID), keep it
                if (off.name && off.name !== off.id) {
                  return off;
                }
                
                const userId = off.id;
                if (!userId) return off;
                
                // Check cache first
                const cachedEmployee = employeeDetailsCacheRef.current.get(userId);
                if (cachedEmployee && cachedEmployee.user_name) {
                  hasUpdates = true;
                  return {
                    ...off,
                    name: cachedEmployee.user_name
                  };
                }
                
                // Try to fetch from API
                try {
                  // Try leaveApi first
                  const officerDetails = await leaveApi.getUserInfo(userId);
                  const name = officerDetails?.user_name || officerDetails?.name;
                  if (name) {
                    employeeDetailsCacheRef.current.set(userId, {
                      user_name: name,
                      timestamp: Date.now()
                    });
                    hasUpdates = true;
                    return {
                      ...off,
                      name: name
                    };
                  }
                } catch (error) {
                  // Try driverApi as fallback
                  try {
                    const driverDetails = await driverApi.fetchDriverDetails(userId);
                    const name = driverDetails?.user_name || driverDetails?.name;
                    if (name) {
                      employeeDetailsCacheRef.current.set(userId, {
                        user_name: name,
                        timestamp: Date.now()
                      });
                      hasUpdates = true;
                      return {
                        ...off,
                        name: name
                      };
                    }
                  } catch (error2) {
                    console.error(`Error fetching name for ${userId}:`, error2);
                  }
                }
                
                // Return as-is if we couldn't fetch a name
                return off;
              })
            );
            
            return {
              ...entry,
              officers: updatedOfficersArray
            };
          })
        );
        
        // Only update state if we actually fetched some names
        if (hasUpdates) {
          setOfficers(updatedOfficers);
        }
      };
      
      // Check if any officers need name fetching
      const needsNameFetch = officers.some(entry => 
        entry.officers?.some(off => !off.name || off.name === off.id)
      );
      
      if (needsNameFetch) {
        fetchMissingNames();
      }
    }
  }, [isEditMode, officers.length]); // Only depend on length to avoid infinite loops

  // Update vehicle and officer names after available data is loaded (for edit mode)
  useEffect(() => {
    if (isEditMode && officers.length > 0 && (availableVehicles.length > 0 || availableOfficers.length > 0)) {
      setOfficers(prevOfficers => prevOfficers.map(entry => {
        const vehicle = availableVehicles.length > 0 
          ? availableVehicles.find(v => (v.vehicle_id || v.id) === entry.vehicle.id)
          : null;
        
        // Update officers within the entry
        let updatedOfficersArray = entry.officers;
        if (entry.officers && Array.isArray(entry.officers) && availableOfficers.length > 0) {
          updatedOfficersArray = entry.officers.map(off => {
            const availableOfficer = availableOfficers.find(o => 
              (o.user_id || o.id)?.toString() === off.id?.toString()
            );
            // Only update if we found a better name in availableOfficers
            if (availableOfficer && (availableOfficer.user_name || availableOfficer.name)) {
              const newName = availableOfficer.user_name || availableOfficer.name;
              // Only update if current name is missing or is just the ID
              if (!off.name || off.name === off.id) {
                return {
                  ...off,
                  name: newName
                };
              }
            }
            return off;
          });
        }
        
        return {
          ...entry,
          vehicle: {
            ...entry.vehicle,
            name: vehicle ? (vehicle.model_name || vehicle.registration_no || entry.vehicle.name) : entry.vehicle.name
          },
          officers: updatedOfficersArray,
          note: entry.note || ''
        };
      }));
    }
  }, [isEditMode, availableVehicles, availableOfficers]);

  // Add first officer unconditionally
  const addFirstOfficer = () => {
    const isOnFoot = patrolData?.patrol_type === 'on-foot';
    setOfficers([{
      officers: [], // Array of selected officers
      vehicle: { id: null, name: null },
      note: '',
      material: null,
    }]);
    setTotalOfficersAssigned(1);
    setFieldErrors([createFieldErrorState(isOnFoot)]);
  };

  // Load existing convoy details for edit mode
  const loadExistingConvoy = async () => {
    if (!patrolIdParam) {
      console.warn('⚠️ No patrolIdParam provided');
      return;
    }
    
    try {
      console.log('🔄 Loading existing convoy for patrol:', patrolIdParam);
      console.log('📦 patrolData:', patrolData);
      console.log('📦 originalPatrol from state:', originalPatrol);
      
      // First, try to use originalPatrol data if available (passed from CreatePatrollingSchedule)
      let patrol = originalPatrol;
      
      // Check if patrol data has originalPatrol embedded
      if (!patrol && patrolData?.originalPatrol) {
        patrol = patrolData.originalPatrol;
        console.log('✅ Found originalPatrol in patrolData:', patrol);
      }
      
      // If not available, try to get from localStorage
      if (!patrol) {
        const storedPatrolData = localStorage.getItem('editPatrolData');
        if (storedPatrolData) {
          try {
            patrol = JSON.parse(storedPatrolData);
            console.log('✅ Loaded patrol data from localStorage:', patrol);
          } catch (e) {
            console.error('Error parsing stored patrol data:', e);
          }
        }
      }
      
      // If still not available, fetch from API
      if (!patrol) {
        console.log('🔄 Fetching patrol data from API...');
        patrol = await patrollingApi.getById(patrolIdParam);
        console.log('✅ Fetched patrol data from API:', patrol);
      }
      
      if (!patrol) {
        console.error('❌ Failed to load patrol data from any source');
        throw new Error('Failed to load patrol data');
      }
      
      if (patrol && patrol.patrol_details && Array.isArray(patrol.patrol_details) && patrol.patrol_details.length > 0) {
        console.log('✅ Found patrol_details:', patrol.patrol_details);
        
        // Convert patrol_details to the format expected by the component
        // patrol_details structure: [{ vehicle_id, user_ids: [], note, material }, ...]
        const loadedEntries = [];
        
        for (const detail of patrol.patrol_details) {
          const entry = {
            vehicle: {
              id: detail.vehicle_id || null,
              name: null // Will be fetched later
            },
            note: detail.note || detail.notes || '',
            material: detail.material || '',
            officers: []
          };
          
          // Fetch officer names for user_ids
          if (detail.user_ids && Array.isArray(detail.user_ids)) {
            const officersWithNames = await Promise.all(
              detail.user_ids.map(async (userId) => {
                // Check cache first
                const cachedEmployee = employeeDetailsCacheRef.current.get(userId);
                if (cachedEmployee && cachedEmployee.user_name) {
                  console.log(`✅ Using cached employee name for ${userId}: ${cachedEmployee.user_name}`);
                  return {
                    id: userId,
                    name: cachedEmployee.user_name
                  };
                }
                
                try {
                  // Try to fetch officer details from leaveApi first
                  console.log(`🔄 Fetching user info for userId: ${userId}`);
                  const officerDetails = await leaveApi.getUserInfo(userId);
                  console.log(`✅ Fetched user info:`, officerDetails);
                  
                  const name = officerDetails?.user_name || officerDetails?.name;
                  if (name) {
                    // Cache the result
                    employeeDetailsCacheRef.current.set(userId, {
                      user_name: name,
                      timestamp: Date.now()
                    });
                    return {
                      id: userId,
                      name: name
                    };
                  } else {
                    // No name from leaveApi, try driverApi
                    console.log(`⚠️ No name from leaveApi for ${userId}, trying driverApi...`);
                    throw new Error('No name from leaveApi');
                  }
                } catch (error) {
                  console.error(`❌ Error fetching officer details for ${userId} from leaveApi:`, error);
                  // Fallback: try driverApi as backup
                  try {
                    console.log(`🔄 Fetching driver details for userId: ${userId}`);
                    const driverDetails = await driverApi.fetchDriverDetails(userId);
                    const name = driverDetails?.user_name || driverDetails?.name;
                    
                    if (name) {
                      // Cache the result
                      employeeDetailsCacheRef.current.set(userId, {
                        user_name: name,
                        timestamp: Date.now()
                      });
                      console.log(`✅ Fetched and cached employee name from driverApi for ${userId}: ${name}`);
                      return {
                        id: userId,
                        name: name
                      };
                    } else {
                      console.warn(`⚠️ No name found from driverApi for ${userId}`);
                      return {
                        id: userId,
                        name: userId // Fallback to userId if no name found
                      };
                    }
                  } catch (error2) {
                    console.error(`❌ Error fetching driver details for ${userId}:`, error2);
                    return {
                      id: userId,
                      name: userId // Fallback to userId if all APIs fail
                    };
                  }
                }
              })
            );
            entry.officers = officersWithNames;
            console.log(`✅ Officers with names for this entry:`, entry.officers);
          }
          
          loadedEntries.push(entry);
        }
        
        console.log('✅ Loaded entries:', loadedEntries);
        console.log('✅ Loaded entries with officer details:', JSON.stringify(loadedEntries, null, 2));
        
        if (loadedEntries.length > 0) {
          setOfficers(loadedEntries);
          setTotalOfficersAssigned(loadedEntries.length);
          setFieldErrors(loadedEntries.map(() => createFieldErrorState(patrolData?.patrol_type === 'on-foot')));
          console.log('✅ Successfully set officers state with', loadedEntries.length, 'entries');
          
          // Log what's in the state after setting
          setTimeout(() => {
            console.log('🔍 Officers state after setting:', officers);
          }, 100);
        } else {
          console.log('⚠️ loadedEntries is empty, adding first officer');
          addFirstOfficer();
        }
      } else {
        console.log('⚠️ No patrol_details found in patrol data');
        console.log('Patrol object:', patrol);
        // No officers found, add first officer
        addFirstOfficer();
      }
    } catch (error) {
      console.error('❌ Error loading existing convoy:', error);
      console.error('Error details:', error.message, error.stack);
      // Fallback to adding first officer
      addFirstOfficer();
    }
  };

  // Add a new officer
  const addOfficer = () => {
    const isOnFoot = patrolData?.patrol_type === 'on-foot';
    // In edit mode, allow adding officers even if limit is reached (limit might not be accurate)
    // In create mode, respect the vehicle limit (since each card needs a vehicle) - unless on-foot patrol
    if (isEditMode || isOnFoot || officers.length < totalVehiclesAvailable || totalVehiclesAvailable <= 0) {
      setOfficers(prev => [...prev, {
        officers: [], // Array of selected officers
        vehicle: { id: null, name: null },
        note: '',
        material: null,
      }]);
      setTotalOfficersAssigned(prev => prev + 1);
      setFieldErrors(prev => [...prev, createFieldErrorState(isOnFoot)]);
    } else {
      alert('Cannot add more vehicles. Maximum limit of available vehicles reached!');
    }
  };

  // Remove an officer
  const removeOfficer = (index) => {
    if (officers.length <= 1) {
      alert('At least one officer is required');
      return;
    }
    
    setOfficers(prev => prev.filter((_, i) => i !== index));
    setTotalOfficersAssigned(prev => prev - 1);
    setFieldErrors(prev => prev.filter((_, i) => i !== index));
  };

  // Validate officer details
  const validateOfficerDetails = () => {
    const isOnFoot = patrolData?.patrol_type === 'on-foot';
    const errors = officers.map(officer => ({
      officer: !officer.officers || !Array.isArray(officer.officers) || officer.officers.length === 0,
      vehicle: isOnFoot ? false : !officer.vehicle.id, // Skip vehicle validation for on-foot patrol
      note: !officer.note || officer.note.trim() === '',
      material: !officer.material || officer.material.trim() === '',
    }));

    const isValid = errors.every(errorSet =>
      !errorSet.officer && !errorSet.vehicle && !errorSet.note && !errorSet.material
    );

    setFieldErrors(isValid ? officers.map(() => createFieldErrorState(isOnFoot)) : errors);
    return isValid;
  };

  const clearFieldError = (index, field) => {
    setFieldErrors(prev => {
      const next = [...prev];
      while (next.length <= index) {
        next.push(createFieldErrorState());
      }
      next[index] = {
        ...next[index],
        [field]: false,
      };
      return next;
    });
    setValidationMessage('');
  };

  // Handle officer selection
  const handleOfficerSelection = (index) => {
    setCurrentOfficerIndex(index);
    setShowOfficerDialog(true);
  };

  const handleOfficerConfirm = async (selectedEmployees) => {
    console.log('handleOfficerConfirm called with:', selectedEmployees);
    
    if (!selectedEmployees || !Array.isArray(selectedEmployees) || selectedEmployees.length === 0 || currentOfficerIndex === null) {
      console.log('Invalid officer selection or index');
      return;
    }
    
    // First, check if any selected employee is already assigned to another group
    const assignedOfficerIds = new Set();
    officers.forEach((o, i) => {
      if (i !== currentOfficerIndex && o.officers && Array.isArray(o.officers)) {
        o.officers.forEach(off => {
          if (off.id) {
            assignedOfficerIds.add(off.id.toString());
          }
        });
      }
    });
    
    // Validate that selected employees are not already assigned to other groups
    const duplicateEmployees = [];
    selectedEmployees.forEach(employee => {
      const employeeId = (employee?.id || employee?.user_id || employee)?.toString();
      if (employeeId && assignedOfficerIds.has(employeeId)) {
        // Find the employee name for better error message
        const existingOfficer = officers.find((o, i) => 
          i !== currentOfficerIndex && 
          o.officers?.some(off => off.id?.toString() === employeeId)
        );
        const employeeName = employee?.name || employee?.user_name || 
          existingOfficer?.officers?.find(off => off.id?.toString() === employeeId)?.name || 
          employeeId;
        duplicateEmployees.push(employeeName);
      }
    });
    
    if (duplicateEmployees.length > 0) {
      alert(`The following employee(s) are already assigned to another group:\n${duplicateEmployees.join(', ')}\n\nPlease select different employees.`);
      return;
    }
    
    // Map selected employees to officer format with names, and deduplicate by ID
    const selectedOfficersMap = new Map(); // Use Map to deduplicate by employee ID
    
    selectedEmployees.forEach(employee => {
      let officer = null;
      
      // If employee is already an object with id and name, use it directly
      if (typeof employee === 'object' && employee !== null) {
        const id = employee.id || employee.user_id;
        const name = employee.name || employee.user_name;
        
        if (id && name) {
          console.log('Using employee object with name:', { id, name });
          officer = {
            id: id.toString(),
            name: name,
          };
        } else if (id) {
          // If we have ID but no name, try to find in availableOfficers
          const availableOfficer = availableOfficers.find(o => 
            (o.user_id || o.id)?.toString() === id.toString()
          );
          
          if (availableOfficer) {
            console.log(`Found officer in availableOfficers:`, availableOfficer);
            officer = {
              id: id.toString(),
              name: availableOfficer.user_name || availableOfficer.name || id.toString(),
            };
          } else {
            console.warn(`Officer ${id} not found in availableOfficers, using ID as name`);
            officer = {
              id: id.toString(),
              name: id.toString(),
            };
          }
        }
      } else {
        // If employee is just an ID string (backward compatibility)
        const id = employee?.toString();
        if (id) {
          const availableOfficer = availableOfficers.find(o => 
            (o.user_id || o.id)?.toString() === id
          );
          
          if (availableOfficer) {
            console.log(`Found officer in availableOfficers:`, availableOfficer);
            officer = {
              id: id,
              name: availableOfficer.user_name || availableOfficer.name || id,
            };
          } else {
            console.warn(`Officer ${id} not found, using ID as name`);
            officer = {
              id: id,
              name: id,
            };
          }
        }
      }
      
      // Only add to map if officer is valid and not already present (deduplication)
      if (officer && officer.id) {
        if (!selectedOfficersMap.has(officer.id)) {
          selectedOfficersMap.set(officer.id, officer);
        } else {
          console.log(`Skipping duplicate employee: ${officer.name} (ID: ${officer.id})`);
        }
      }
    });
    
    const validOfficers = Array.from(selectedOfficersMap.values());
    console.log('Valid officers to assign with names:', validOfficers);
    
    if (validOfficers.length === 0) {
      console.log('No valid officers to assign');
      return;
    }
    
    setOfficers(prev => {
      const updated = [...prev];
      const currentOfficer = updated[currentOfficerIndex] || {};
      updated[currentOfficerIndex] = {
        ...currentOfficer,
        officers: validOfficers,
        vehicle: currentOfficer.vehicle || { id: null, name: null },
        note: currentOfficer.note || '',
        material: currentOfficer.material || null,
      };
      console.log('Updated officers array with names:', updated);
      return updated;
    });

    clearFieldError(currentOfficerIndex, 'officer');
    
    setShowOfficerDialog(false);
    setCurrentOfficerIndex(null);
  };

  // Get group assignments for employees (employeeId -> group number)
  const getEmployeeGroupAssignments = () => {
    const groupAssignments = new Map(); // Map<employeeId, groupNumber>
    
    officers.forEach((o, i) => {
      // Skip the current card being edited
      if (i !== currentOfficerIndex && o.officers && Array.isArray(o.officers)) {
        o.officers.forEach(off => {
          const officerId = off.id?.toString();
          if (officerId) {
            // Store group number (1-indexed for display)
            groupAssignments.set(officerId, i + 1);
          }
        });
      }
    });
    
    return groupAssignments;
  };

  // Get available officers for selection (filter out already assigned)
  const getAvailableOfficersForSelection = () => {
    if (currentOfficerIndex === null) return [];
    
    // Get all officer IDs that are already selected in other cards
    const assignedOfficerIds = new Set();
    officers.forEach((o, i) => {
      // Skip the current card being edited
      if (i !== currentOfficerIndex && o.officers && Array.isArray(o.officers)) {
        o.officers.forEach(off => {
          // Handle both string and number IDs
          const officerId = off.id?.toString();
          if (officerId) {
            assignedOfficerIds.add(officerId);
          }
        });
      }
    });
    
    // Filter available officers to exclude those already assigned to other groups
    const filteredOfficers = availableOfficers.filter(availableOfficer => {
      const officerId = (availableOfficer.user_id || availableOfficer.id)?.toString();
      // Include only if not assigned to any other card
      const isAvailable = !assignedOfficerIds.has(officerId);
      
      if (!isAvailable) {
        console.log(`Officer ${officerId} (${availableOfficer.user_name || availableOfficer.name}) is already assigned to another group`);
      }
      
      return isAvailable;
    });
    
    console.log(`Available officers for group ${currentOfficerIndex + 1}:`, filteredOfficers.length, 'out of', availableOfficers.length);
    
    return filteredOfficers;
  };

  // Handle vehicle selection
  const handleVehicleSelection = (index) => {
    setCurrentOfficerIndex(index);
    setShowVehicleDialog(true);
  };

  const handleVehicleConfirm = (vehicleId) => {
    if (!vehicleId || currentOfficerIndex === null) return;
    
    const selectedVehicle = availableVehicles.find(v => 
      v.vehicle_id === vehicleId || v.id === vehicleId
    );
    
    if (!selectedVehicle) return;
    
    setOfficers(prev => {
      const updated = [...prev];
      updated[currentOfficerIndex] = {
        ...updated[currentOfficerIndex],
        vehicle: {
          id: selectedVehicle.vehicle_id || selectedVehicle.id,
          name: selectedVehicle.model_name || selectedVehicle.registration_no,
        },
      };
      return updated;
    });

    clearFieldError(currentOfficerIndex, 'vehicle');
    
    setShowVehicleDialog(false);
    setCurrentOfficerIndex(null);
  };

  // Handle note input change
  const handleNoteChange = (index, value) => {
    setOfficers(prev => {
      const updated = [...prev];
      updated[index] = {
        ...updated[index],
        note: value,
      };
      return updated;
    });

    if (value && value.trim()) {
      clearFieldError(index, 'note');
    }
  };

  // Handle material input change
  const handleMaterialChange = (index, value) => {
    setOfficers(prev => {
      const updated = [...prev];
      updated[index] = {
        ...updated[index],
        material: value,
      };
      return updated;
    });

    if (value && value.trim()) {
      clearFieldError(index, 'material');
    }
  };

  // Get available vehicles for selection (filter out already assigned)
  const getAvailableVehiclesForSelection = () => {
    if (currentOfficerIndex === null) return [];
    
    const currentOfficer = officers[currentOfficerIndex];
    return availableVehicles.filter(vehicle => {
      const vehicleId = vehicle.vehicle_id || vehicle.id;
      // Include if not assigned to any other officer (except current officer)
      return !officers.some((o, i) => 
        i !== currentOfficerIndex && o.vehicle.id === vehicleId
      );
    });
  };

  // Handle preview toggle
  const handlePreviewToggle = () => {
    if (isPreview) {
      setIsPreview(false);
      setValidationMessage('');
      return;
    }

    if (!validateOfficerDetails()) {
      setValidationMessage('Please fill all the details before previewing');
      return;
    }

    setValidationMessage('');
    setIsPreview(true);
  };

  // Helper function to convert date and time to ISO8601 timestamp in IST
  const convertToISO8601 = (dateStr, timeStr) => {
    try {
      // dateStr is in format "DD-MM-YYYY"
      // timeStr is in format "HH:MM AM/PM"
      // We need to treat this as IST (UTC+5:30) and convert to ISO8601
      
      // Parse date
      const [day, month, year] = dateStr.split('-').map(Number);
      
      // Parse time
      const timeMatch = timeStr.match(/(\d+):(\d+)\s*(AM|PM)/i);
      if (!timeMatch) {
        throw new Error('Invalid time format');
      }
      
      let hours = parseInt(timeMatch[1]);
      const minutes = parseInt(timeMatch[2]);
      const period = timeMatch[3].toUpperCase();
      
      // Convert to 24-hour format
      if (period === 'PM' && hours !== 12) {
        hours += 12;
      } else if (period === 'AM' && hours === 12) {
        hours = 0;
      }
      
      // Create date string in IST format (UTC+5:30)
      // Format: YYYY-MM-DDTHH:mm:ss+05:30
      const yearStr = String(year).padStart(4, '0');
      const monthStr = String(month).padStart(2, '0');
      const dayStr = String(day).padStart(2, '0');
      const hoursStr = String(hours).padStart(2, '0');
      const minutesStr = String(minutes).padStart(2, '0');
      
      // Return ISO8601 format with IST timezone (+05:30)
      return `${yearStr}-${monthStr}-${dayStr}T${hoursStr}:${minutesStr}:00+05:30`;
    } catch (error) {
      console.error('Error converting date/time to ISO8601:', error);
      throw new Error(`Invalid date/time format: ${dateStr} ${timeStr}`);
    }
  };

  // Handle form submission
  const handleSubmit = async () => {
    if (!validateOfficerDetails()) {
      setValidationMessage('Please fill all the details before submitting');
      return;
    }

    setValidationMessage('');

    // Debug: Log officers array before processing
    console.log('Officers array before processing:', officers);

    try {
      setIsSubmitting(true);
      setError(null);
      
      if (isEditMode && patrolIdParam) {
        // Update existing schedule using the patrolling API
        
        // Convert dates and times to ISO8601 format
        const patrolStartTimestamp = convertToISO8601(
          patrolData.start_date,
          patrolData.start_time
        );
        const patrolEndTimestamp = convertToISO8601(
          patrolData.end_date,
          patrolData.end_time
        );
        
        // Build QR sequence: start_qr + qr_scanners + end_qr
        const qrSequence = [
          patrolData.start_qr.id,
          ...(patrolData.qr_scanners || []).map(qr => qr.id),
          patrolData.end_qr.id
        ];
        
        // Group officers by vehicle/note/material combination
        const patrolDetailsMap = new Map();
        
        officers.forEach((entry) => {
          if (entry.officers && Array.isArray(entry.officers) && entry.officers.length > 0) {
            // Create a unique key for this combination
            const vehicleId = entry.vehicle?.id || null;
            const note = entry.note || '';
            const material = entry.material || '';
            const key = `${vehicleId}-${note}-${material}`;
            
            if (!patrolDetailsMap.has(key)) {
              patrolDetailsMap.set(key, {
                vehicle_id: vehicleId,
                user_ids: [],
                note: note,
                material: material,
              });
            }
            
            // Add all user IDs from this entry
            const userIds = entry.officers.map(off => off.id).filter(Boolean);
            patrolDetailsMap.get(key).user_ids.push(...userIds);
          }
        });
        
        // Convert map to array
        const patrolDetails = Array.from(patrolDetailsMap.values());
        
        // Prepare API payload for update
        const updatePayload = {
          start_qr_id: patrolData.start_qr.id,
          end_qr_id: patrolData.end_qr.id,
          qr_sequence: qrSequence,
          patrol_start_timestamp: patrolStartTimestamp,
          patrol_end_timestamp: patrolEndTimestamp,
          patrol_type: patrolData.patrol_type === 'on-foot' ? 'on_foot' : 'vehicle',
          patrol_details: patrolDetails,
        };

        console.log('Patrol Update Payload:', updatePayload);
        
        // Call the patrolling UPDATE API
        const response = await patrollingApi.updatePatrol(patrolIdParam, updatePayload);
        
        console.log('Patrol Update Response:', response);
        
        // Clear localStorage
        localStorage.removeItem('pendingPatrolData');
        localStorage.removeItem('createPatrolFormData');
        localStorage.removeItem('editPatrolData');
        
        // Set flag to refresh patrol schedules when returning to list view
        sessionStorage.setItem('shouldRefreshPatrolSchedules', 'true');
        
        // Show success message and navigate back
        alert(response.message || 'Patrol schedule updated successfully!');
        setSearchParams({});
      } else {
        // Create new schedule using the patrolling API
        
        // Convert dates and times to ISO8601 format
        const patrolStartTimestamp = convertToISO8601(
          patrolData.start_date,
          patrolData.start_time
        );
        const patrolEndTimestamp = convertToISO8601(
          patrolData.end_date,
          patrolData.end_time
        );
        
        // Build QR sequence: start_qr + qr_scanners + end_qr
        const qrSequence = [
          patrolData.start_qr.id,
          ...(patrolData.qr_scanners || []).map(qr => qr.id),
          patrolData.end_qr.id
        ];
        
        // Group officers by vehicle/note/material combination
        const patrolDetailsMap = new Map();
        
        officers.forEach((entry) => {
          if (entry.officers && Array.isArray(entry.officers) && entry.officers.length > 0) {
            // Create a unique key for this combination
            const vehicleId = entry.vehicle?.id || null;
            const note = entry.note || '';
            const material = entry.material || '';
            const key = `${vehicleId}-${note}-${material}`;
            
            if (!patrolDetailsMap.has(key)) {
              patrolDetailsMap.set(key, {
                vehicle_id: vehicleId,
                user_ids: [],
                note: note,
                material: material,
              });
            }
            
            // Add all user IDs from this entry
            const userIds = entry.officers.map(off => off.id).filter(Boolean);
            patrolDetailsMap.get(key).user_ids.push(...userIds);
          }
        });
        
        // Convert map to array and filter out entries without vehicle_id for vehicle patrols
        const patrolDetails = Array.from(patrolDetailsMap.values());
        
        // Prepare API payload
        const apiPayload = {
          start_qr_id: patrolData.start_qr.id,
          end_qr_id: patrolData.end_qr.id,
          qr_sequence: qrSequence,
          patrol_start_timestamp: patrolStartTimestamp,
          patrol_end_timestamp: patrolEndTimestamp,
          patrol_type: patrolData.patrol_type === 'on-foot' ? 'on_foot' : 'vehicle',
          patrol_details: patrolDetails,
        };

        console.log('Patrol API Payload:', apiPayload);
        
        // Call the patrolling API
        const response = await patrollingApi.createPatrol(apiPayload);
        
        console.log('Patrol API Response:', response);
        
        // Clear localStorage
        localStorage.removeItem('pendingPatrolData');
        localStorage.removeItem('createPatrolFormData');
        
        // Set flag to refresh patrol schedules when returning to list view
        sessionStorage.setItem('shouldRefreshPatrolSchedules', 'true');
        
        // Show success message and navigate back
        alert(response.message || 'Patrol schedule created successfully!');
        setSearchParams({});
      }
    } catch (error) {
      console.error(`Error ${isEditMode ? 'updating' : 'creating'} patrol schedule:`, error);
      alert(`Failed to ${isEditMode ? 'update' : 'create'} schedule: ${error.message}`);
      setError(error.message);
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isLoading || !patrolData) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="text-gray-500">Loading...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="w-full h-full flex flex-col">
        <div className="mb-6 flex items-center gap-4">
          <button
            onClick={() => setSearchParams({ view: 'create-patrol' })}
            className="p-2 rounded-lg hover:bg-gray-100 transition-colors"
            title="Back"
          >
            <ArrowLeft className="w-6 h-6 text-gray-600" />
          </button>
          <div>
            <h2 className={typography.h2} style={{ color: '#000000' }}>
              Patrol Officer Assignment
            </h2>
          </div>
        </div>
        <div className="mb-4 p-4 bg-red-100 border border-red-400 text-red-700 rounded-lg">
          {error}
        </div>
      </div>
    );
  }

  return (
    <div className="w-full h-full flex flex-col">
      {/* Header Section */}
      <div className="mb-6 flex items-center gap-4">
        <button
          onClick={() => setSearchParams({ view: 'create-patrol' })}
          className="p-2 rounded-lg hover:bg-gray-100 transition-colors"
          title="Back"
        >
          <ArrowLeft className="w-6 h-6 text-gray-600" />
        </button>
        <div className="flex-1">
          <h2 className={typography.h2} style={{ color: '#000000' }}>
            {isEditMode ? 'Edit Patrol Officers' : 'Patrol Officer Assignment'}
          </h2>
          <p className={typography.p} style={{ color: '#666666', marginTop: '0.5rem' }}>
            {isEditMode 
              ? 'Update patrol officers for the patrol'
              : 'Assign patrol officers for the patrol'}
          </p>
        </div>
      </div>

      {/* Stats Section */}
      <Card.Gradient className="mb-6 p-6 bg-green-600">
        <div className="flex justify-between items-start mb-4">
          <div className="flex-1">
            {!isOnFootPatrol && (
              <>
                <p className={typography.h4} style={{ color: '#000000', marginBottom: '0.75rem' }}>
                  {totalVehiclesAvailable === -1 
                    ? 'Total No. of Vehicles Available: Loading...'
                    : `Total No. of Vehicles Available: ${totalVehiclesAvailable}`}
                </p>
                <p className={typography.h4} style={{ color: '#000000' }}>
                  Total No. of Vehicles Assigned: {totalOfficersAssigned}
                </p>
              </>
            )}
            {isOnFootPatrol && (
              <p className={typography.h4} style={{ color: '#000000' }}>
                Patrol Type: On Foot
              </p>
            )}
          </div>
          <button
            onClick={() => setShowPatrolInfoDialog(true)}
            className="p-2 rounded-lg hover:bg-green-700 transition-colors"
            title="View Patrol Information"
          >
            <Info className="w-6 h-6 text-white" />
          </button>
        </div>
        
        <div className="flex gap-3 justify-center">
          <PrimaryButton
            onClick={handlePreviewToggle}
            className="flex-1"
          >
            {isPreview ? 'Edit' : 'Check Preview'}
          </PrimaryButton>
          {isPreview && (
            <PrimaryButton
              onClick={handleSubmit}
              disabled={isSubmitting}
              className="flex-1"
            >
              {isSubmitting ? 'Submitting...' : 'Submit'}
            </PrimaryButton>
          )}
        </div>
        {validationMessage && (
          <div className="mt-4 p-3 rounded border border-red-400 bg-red-50 text-red-700 text-sm text-center">
            {validationMessage}
          </div>
        )}
      </Card.Gradient>

      {/* Officers List */}
      <div className="flex-1 overflow-y-auto mb-6">
        {isEditMode ? (
          // In edit mode, show assigned officers (even if no available officers)
          officers.length === 0 ? (
            <div className="text-center py-8">
              <p className={typography.p} style={{ color: '#000000' }}>
                No officers assigned to this patrol. Please assign at least one officer.
              </p>
            </div>
          ) : (
            <div className="space-y-4">
              {officers.map((officer, index) => {
                const officerErrors = fieldErrors[index] || {};
                return (
                <Card.Gradient key={index} className="p-6 border-2 border-green-400">
                <div className="flex justify-between items-start mb-4">
                  <h5 className={typography.h5} style={{ color: '#000000' }}>
                    {isOnFootPatrol ? `Officer Group • ${index + 1}` : `Assigned Vehicle • ${index + 1}`}
                  </h5>
                  {officers.length > 1 && (
                    <button
                      onClick={() => removeOfficer(index)}
                      disabled={isPreview}
                      className={`p-1 rounded transition-colors ${
                        isPreview 
                          ? 'opacity-50 cursor-not-allowed' 
                          : 'hover:bg-gray-100'
                      }`}
                    >
                      <X className={`w-5 h-5 ${isPreview ? 'text-gray-400' : 'text-gray-600'}`} />
                    </button>
                  )}
                </div>

                {/* Vehicle Selection - Hidden for on-foot patrol */}
                {!isOnFootPatrol && (
                  <div className="mb-4">
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Vehicle
                    </label>
                    <button
                      onClick={() => handleVehicleSelection(index)}
                      disabled={isPreview}
                      className={`w-full h-12 border rounded px-3 bg-white flex items-center gap-3 transition-colors ${
                        isPreview
                          ? 'border-gray-300 bg-gray-100 cursor-not-allowed'
                          : officerErrors.vehicle
                          ? 'border-red-500 hover:bg-gray-50'
                          : officer.vehicle?.id
                          ? 'border-green-500 hover:bg-gray-50'
                          : 'border-gray-300 hover:bg-gray-50'
                      }`}
                    >
                      <Truck className={`w-5 h-5 ${
                        officer.vehicle?.id
                          ? 'text-green-600'
                          : officerErrors.vehicle
                          ? 'text-red-500'
                          : 'text-gray-400'
                      }`} />
                      <span className={`flex-1 text-left ${
                        officer.vehicle?.id
                          ? 'text-black'
                          : officerErrors.vehicle
                          ? 'text-red-600'
                          : 'text-gray-500'
                      }`}>
                        {officer.vehicle?.name || officer.vehicle?.id || 'Select Vehicle'}
                      </span>
                    </button>
                  </div>
                )}

                  {/* Officer Selection */}
                  <div className="mb-4">
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Patrolling Officers {officer.officers && officer.officers.length > 0 && `(${officer.officers.length} selected)`}
                    </label>
                    <button
                      onClick={() => handleOfficerSelection(index)}
                      disabled={isPreview}
                      className={`w-full min-h-12 border-2 rounded px-3 py-2 bg-white flex items-start gap-3 transition-colors ${
                        isPreview
                          ? 'border-gray-300 bg-gray-100 cursor-not-allowed'
                          : officerErrors.officer
                          ? 'border-red-500 hover:bg-gray-50'
                          : officer.officers && officer.officers.length > 0
                          ? 'border-green-500 hover:bg-gray-50'
                          : 'border-gray-300 hover:bg-gray-50'
                      }`}
                    >
                      <User className={`w-5 h-5 mt-1 flex-shrink-0 ${
                        officer.officers && officer.officers.length > 0
                          ? 'text-green-600'
                          : officerErrors.officer
                          ? 'text-red-500'
                          : 'text-gray-400'
                      }`} />
                      <div className="flex-1 text-left">
                        {officer.officers && officer.officers.length > 0 ? (
                          <div className="flex flex-wrap gap-2">
                            {officer.officers.map((off, idx) => (
                              <span
                                key={off.id || idx}
                                className="inline-flex items-center px-3 py-1.5 rounded-md bg-green-100 text-green-800 text-sm font-medium border border-green-300"
                              >
                                {off.name && off.name !== off.id ? off.name : 'Unknown Employee'}
                              </span>
                            ))}
                          </div>
                        ) : (
                          <span className={`${
                            officerErrors.officer
                              ? 'text-red-600'
                              : 'text-gray-500'
                          }`}>
                            Select Patrolling Officers
                          </span>
                        )}
                      </div>
                    </button>
                  </div>

                  {/* Material Input */}
                  <div className="mb-4">
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Material transferring/Ticket_id
                    </label>
                    <input
                      type="text"
                      value={officer.material || ''}
                      onChange={(e) => handleMaterialChange(index, e.target.value)}
                      disabled={isPreview}
                      placeholder="Enter material or ticket ID"
                      className={`w-full h-12 px-3 border rounded bg-white focus:outline-none focus:ring-2 ${
                        isPreview
                          ? 'border-gray-300 bg-gray-100 cursor-not-allowed'
                          : officerErrors.material
                          ? 'border-red-500 focus:ring-red-500'
                          : officer.material
                          ? 'border-green-500 focus:ring-green-500'
                          : 'border-gray-300 focus:ring-green-500'
                      }`}
                    />
                  </div>

                  {/* Note Input */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Note
                    </label>
                    <textarea
                      value={officer.note || ''}
                      onChange={(e) => handleNoteChange(index, e.target.value)}
                      disabled={isPreview}
                      placeholder="Enter note"
                      rows={3}
                      className={`w-full px-3 py-2 border rounded bg-white focus:outline-none focus:ring-2 ${
                        isPreview
                          ? 'border-gray-300 bg-gray-100 cursor-not-allowed'
                          : officerErrors.note
                          ? 'border-red-500 focus:ring-red-500'
                          : officer.note
                          ? 'border-green-500 focus:ring-green-500'
                          : 'border-gray-300 focus:ring-green-500'
                      }`}
                    />
                  </div>
                  </Card.Gradient>
                );
              })}
            </div>
          )
        ) : totalOfficersAvailable === 0 ? (
          <div className="text-center py-8">
            <p className={typography.p} style={{ color: '#000000' }}>
              No officers available for the selected time period. Please try a different time.
            </p>
          </div>
        ) : (
          <div className="space-y-4">
            {officers.map((officer, index) => {
              const officerErrors = fieldErrors[index] || {};
              return (
                <Card.Gradient key={index} className="p-6 border-2 border-green-400">
                <div className="flex justify-between items-start mb-4">
                  <h5 className={typography.h5} style={{ color: '#000000' }}>
                    {isOnFootPatrol ? `Officer Group • ${index + 1}` : `Vehicle • ${index + 1}`}
                  </h5>
                  {officers.length > 1 && (
                    <button
                      onClick={() => removeOfficer(index)}
                      disabled={isPreview}
                      className={`p-1 rounded transition-colors ${
                        isPreview 
                          ? 'opacity-50 cursor-not-allowed' 
                          : 'hover:bg-gray-100'
                      }`}
                    >
                      <X className={`w-5 h-5 ${isPreview ? 'text-gray-400' : 'text-gray-600'}`} />
                    </button>
                  )}
                </div>

                {/* Vehicle Selection - Hidden for on-foot patrol */}
                {!isOnFootPatrol && (
                  <div className="mb-4">
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Vehicle
                    </label>
                    <button
                      onClick={() => handleVehicleSelection(index)}
                      disabled={isPreview}
                      className={`w-full h-12 border rounded px-3 bg-white flex items-center gap-3 transition-colors ${
                        isPreview
                          ? 'border-gray-300 bg-gray-100 cursor-not-allowed'
                          : officerErrors.vehicle
                          ? 'border-red-500 hover:bg-gray-50'
                          : officer.vehicle?.id
                          ? 'border-green-500 hover:bg-gray-50'
                          : 'border-gray-300 hover:bg-gray-50'
                      }`}
                    >
                      <Truck className={`w-5 h-5 ${
                        officer.vehicle?.id
                          ? 'text-green-600'
                          : officerErrors.vehicle
                          ? 'text-red-500'
                          : 'text-gray-400'
                      }`} />
                      <span className={`flex-1 text-left ${
                        officer.vehicle?.id
                          ? 'text-black'
                          : officerErrors.vehicle
                          ? 'text-red-600'
                          : 'text-gray-500'
                      }`}>
                        {officer.vehicle?.name || 'Select Vehicle'}
                      </span>
                    </button>
                  </div>
                )}

                {/* Officer Selection */}
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Patrolling Officers {officer.officers && officer.officers.length > 0 && `(${officer.officers.length} selected)`}
                  </label>
                  <button
                    onClick={() => handleOfficerSelection(index)}
                    disabled={isPreview}
                    className={`w-full min-h-12 border-2 rounded px-3 py-2 bg-white flex items-start gap-3 transition-colors ${
                      isPreview
                        ? 'border-gray-300 bg-gray-100 cursor-not-allowed'
                        : officerErrors.officer
                        ? 'border-red-500 hover:bg-gray-50'
                        : officer.officers && officer.officers.length > 0
                        ? 'border-green-500 hover:bg-gray-50'
                        : 'border-gray-300 hover:bg-gray-50'
                    }`}
                  >
                    <User className={`w-5 h-5 mt-1 flex-shrink-0 ${
                      officer.officers && officer.officers.length > 0
                        ? 'text-green-600'
                        : officerErrors.officer
                        ? 'text-red-500'
                        : 'text-gray-400'
                    }`} />
                    <div className="flex-1 text-left">
                      {officer.officers && officer.officers.length > 0 ? (
                        <div className="flex flex-wrap gap-2">
                          {officer.officers.map((off, idx) => (
                            <span
                              key={off.id || idx}
                              className="inline-flex items-center px-3 py-1.5 rounded-md bg-green-100 text-green-800 text-sm font-medium border border-green-300"
                            >
                              {off.name && off.name !== off.id ? off.name : 'Unknown Employee'}
                            </span>
                          ))}
                        </div>
                      ) : (
                        <span className={`${
                          officerErrors.officer
                            ? 'text-red-600'
                            : 'text-gray-500'
                        }`}>
                          Select Patrolling Officers
                        </span>
                      )}
                    </div>
                  </button>
                </div>

                {/* Material Input */}
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Material transferring/Ticket_id
                  </label>
                  <input
                    type="text"
                    value={officer.material || ''}
                    onChange={(e) => handleMaterialChange(index, e.target.value)}
                    disabled={isPreview}
                    placeholder="Enter material or ticket ID"
                    className={`w-full h-12 px-3 border rounded bg-white focus:outline-none focus:ring-2 ${
                      isPreview
                        ? 'border-gray-300 bg-gray-100 cursor-not-allowed'
                        : officerErrors.material
                        ? 'border-red-500 focus:ring-red-500'
                        : officer.material
                        ? 'border-green-500 focus:ring-green-500'
                        : 'border-gray-300 focus:ring-green-500'
                    }`}
                  />
                </div>

                {/* Note Input */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Note
                  </label>
                  <textarea
                    value={officer.note || ''}
                    onChange={(e) => handleNoteChange(index, e.target.value)}
                    disabled={isPreview}
                    placeholder="Enter note"
                    rows={3}
                    className={`w-full px-3 py-2 border rounded bg-white focus:outline-none focus:ring-2 ${
                      isPreview
                        ? 'border-gray-300 bg-gray-100 cursor-not-allowed'
                        : officerErrors.note
                        ? 'border-red-500 focus:ring-red-500'
                        : officer.note
                        ? 'border-green-500 focus:ring-green-500'
                        : 'border-gray-300 focus:ring-green-500'
                    }`}
                  />
                </div>
                </Card.Gradient>
              );
            })}
          </div>
        )}

        {/* Add Officer Button */}
        {(totalVehiclesAvailable > 0 || isOnFootPatrol || (isEditMode && officers.length > 0)) && (
          <button
            onClick={addOfficer}
            disabled={isPreview || (!isOnFootPatrol && totalVehiclesAvailable > 0 && officers.length >= totalVehiclesAvailable)}
            className={`w-full h-12 border-2 rounded-lg flex items-center justify-center transition-colors mt-4 ${
              isPreview || (!isOnFootPatrol && totalVehiclesAvailable > 0 && officers.length >= totalVehiclesAvailable)
                ? 'border-gray-300 bg-gray-100 cursor-not-allowed'
                : 'border-green-400 bg-green-50 hover:bg-green-100'
            }`}
          >
            <Plus className={`w-6 h-6 ${isPreview || (!isOnFootPatrol && totalVehiclesAvailable > 0 && officers.length >= totalVehiclesAvailable) ? 'text-gray-400' : 'text-green-600'}`} />
          </button>
        )}
      </div>

      {/* Officer Selection Dialog */}
      {showOfficerDialog && createPortal(
        <SelectPatrolingEmployees
          isOpen={showOfficerDialog}
          onClose={() => {
            setShowOfficerDialog(false);
            setCurrentOfficerIndex(null);
          }}
          onConfirm={handleOfficerConfirm}
          dialogTitle="Select Patrolling Officers"
          availableEntities={getAvailableOfficersForSelection()}
          initialSelectedIds={
            currentOfficerIndex !== null && officers[currentOfficerIndex]?.officers
              ? officers[currentOfficerIndex].officers.map(o => o.id?.toString()).filter(Boolean)
              : []
          }
          employeeGroupAssignments={Object.fromEntries(getEmployeeGroupAssignments())}
          patrolData={patrolData}
        />,
        document.body
      )}

      {/* Vehicle Selection Dialog */}
      {showVehicleDialog && createPortal(
        <EntitySelectionDialog
          isOpen={showVehicleDialog}
          onClose={() => {
            setShowVehicleDialog(false);
            setCurrentOfficerIndex(null);
          }}
          onConfirm={handleVehicleConfirm}
          entityType="vehicle"
          dialogTitle="Select Vehicle"
          availableEntities={getAvailableVehiclesForSelection()}
        />,
        document.body
      )}


      {/* Patrol Info Dialog */}
      {showPatrolInfoDialog && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-lg w-full max-w-md max-h-[80vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex justify-between items-center mb-4">
                <h3 className={typography.h3} style={{ color: '#000000' }}>
                  Patrol Information
                </h3>
                <button
                  onClick={() => setShowPatrolInfoDialog(false)}
                  className="p-1 hover:bg-gray-100 rounded transition-colors"
                >
                  <X className="w-6 h-6 text-gray-500" />
                </button>
              </div>
              
              <div className="space-y-3">
                {patrolData.patrol_type && (
                  <div>
                    <span className="font-semibold text-gray-700">Patrol Type: </span>
                    <span className="text-gray-900">
                      {patrolData.patrol_type === 'on-foot' ? 'On Foot' : 'Vehicle'}
                    </span>
                  </div>
                )}
                <div>
                  <span className="font-semibold text-gray-700">Start QR: </span>
                  <span className="text-gray-900">{patrolData.start_qr?.address || 'N/A'}</span>
                </div>
                <div>
                  <span className="font-semibold text-gray-700">End QR: </span>
                  <span className="text-gray-900">{patrolData.end_qr?.address || 'N/A'}</span>
                </div>
                <div>
                  <span className="font-semibold text-gray-700">Start Date: </span>
                  <span className="text-gray-900">{patrolData.start_date || 'N/A'}</span>
                </div>
                <div>
                  <span className="font-semibold text-gray-700">Start Time: </span>
                  <span className="text-gray-900">{patrolData.start_time || 'N/A'}</span>
                </div>
                <div>
                  <span className="font-semibold text-gray-700">End Date: </span>
                  <span className="text-gray-900">{patrolData.end_date || 'N/A'}</span>
                </div>
                <div>
                  <span className="font-semibold text-gray-700">End Time: </span>
                  <span className="text-gray-900">{patrolData.end_time || 'N/A'}</span>
                </div>
                {patrolData.qr_scanners && patrolData.qr_scanners.length > 0 && (
                  <div>
                    <span className="font-semibold text-gray-700">QR Scanners: </span>
                    <span className="text-gray-900">
                      {patrolData.qr_scanners.map(qr => qr.address || 'Unknown').join(', ')}
                    </span>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PatrollingAssignConvo;

